import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getTaskById } from '../APIManager/TaskManager'
import { useUserRole } from './useUserRole'
import type { ITaskMetadata, SlotTaskKind, TaskPollData, TaskPollStatus } from '../models/Task'

const TERMINAL_STATUSES = new Set(['SUCCESSFUL', 'WARNING', 'ERROR'])
export const BASE_INTERVAL_MS = 4000
export const MAX_INTERVAL_MS = 60000
// Mobile browsers don't support reliable multi-tab usage — skip visibility machinery.
export const IS_MOBILE = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)

// Owned slotIds currently polled by an active SlotTaskProvider; GlobalSlotTaskPoller skips these.
export const activeSlotsPolling = new Set<string>()

const isTerminal = (status: string): boolean => TERMINAL_STATUSES.has(status)

const getStorageKey = (slotId: string): string => `slotTask_${slotId}`

interface StoredTask {
  taskId: string
  message: string
  successMessage?: string
  /**
   * Ids still to poll once `taskId` finishes, in order. One action can enqueue more than one task
   * — adding a course section to a Survey Project with both default question sets attached seeds
   * each of them (`task_ids`, Backend CR-336) — and the seeds run concurrently server-side, so
   * the user only needs to hear about the last one to finish. Polling them one after another
   * under this single key keeps that as one background run: one running indicator, one action
   * lock, one completion toast.
   */
  queuedTaskIds?: string[]
  /**
   * Persisted with the task, not held in a ref: the toast for a bulk status change is built from
   * the task's own per-section outcomes, and which verb it reads in depends on the operation. A
   * task outlives the modal that started it and survives a reload, so the kind has to travel with
   * it or a reopen finishing after a refresh would be reported in no particular voice.
   */
  kind?: SlotTaskKind
}

const readStorage = (slotId: string): StoredTask | null => {
  try {
    const raw = localStorage.getItem(getStorageKey(slotId))
    return raw ? (JSON.parse(raw) as StoredTask) : null
  } catch {
    return null
  }
}

const writeStorage = (slotId: string, task: StoredTask): void => {
  localStorage.setItem(getStorageKey(slotId), JSON.stringify(task))
}

export type SlotTaskTerminalStatus = Exclude<TaskPollStatus, 'PENDING'>

export interface SlotTaskTerminalResult {
  status: SlotTaskTerminalStatus
  data?: TaskPollData
  taskMetadata?: ITaskMetadata
  /** Stored successMessage or running message — used for SUCCESSFUL toast subtitle. */
  fallbackMessage: string
  /** Set only by tasks whose toast copy depends on which operation ran. */
  kind?: SlotTaskKind
}

export interface UseSlotBackgroundTaskOptions {
  slotId: string
  onTerminal: (result: SlotTaskTerminalResult) => void
}

export interface UseSlotBackgroundTaskReturn {
  isTaskRunning: boolean
  taskMessage: string
  /** An array registers a queue: the ids are polled in order and only the last one is reported. */
  setTask: (taskId: string | string[], message: string, successMessage?: string, onComplete?: () => void, kind?: SlotTaskKind) => void
  clearTask: () => void
  checkTaskNow: () => Promise<boolean>
}

export const useSlotBackgroundTask = ({
  slotId,
  onTerminal
}: UseSlotBackgroundTaskOptions): UseSlotBackgroundTaskReturn => {
  const { isGlobalRole } = useUserRole()
  const stored = readStorage(slotId)

  const [taskId, setTaskId] = useState<string | null>(stored?.taskId ?? null)
  const [queuedTaskIds, setQueuedTaskIds] = useState<string[]>(stored?.queuedTaskIds ?? [])
  const [taskMessage, setTaskMessage] = useState<string>(stored?.message ?? '')
  const [taskSuccessMessage, setTaskSuccessMessage] = useState<string>(stored?.successMessage ?? '')
  const [taskKind, setTaskKind] = useState<SlotTaskKind | undefined>(stored?.kind)
  const [isVisible, setIsVisible] = useState(IS_MOBILE || document.visibilityState === 'visible')
  const handledRef = useRef(false)
  const pollCountRef = useRef(0)

  // Refs: effect re-runs only on taskData change; callbacks and messages are read via ref.
  const taskIdRef = useRef(taskId)
  taskIdRef.current = taskId
  const queuedTaskIdsRef = useRef(queuedTaskIds)
  queuedTaskIdsRef.current = queuedTaskIds
  const taskMessageRef = useRef(taskMessage)
  taskMessageRef.current = taskMessage
  const taskSuccessMessageRef = useRef(taskSuccessMessage)
  taskSuccessMessageRef.current = taskSuccessMessage
  const taskKindRef = useRef(taskKind)
  taskKindRef.current = taskKind
  const onTerminalRef = useRef(onTerminal)
  onTerminalRef.current = onTerminal
  // Per-task callback. Not persisted (closure can't be serialized); only fires within the same mount.
  const onCompleteRef = useRef<(() => void) | null>(null)

  /**
   * Re-seed when the hook is handed a different slot.
   *
   * The `useState` initialisers above run once per mount, so a living hook whose `slotId` changes
   * — the add-to-project modal switching targets, `SlotTaskProvider` re-pointed — kept the
   * previous project's task id, queue and message. `checkIfBlocked` then applied the old run's
   * lock to the new project, or let an edit through because the *previous* slot happened to be
   * idle. Found by Bugbot on #1948.
   *
   * Guarded on the slot the state actually belongs to rather than on `slotId` alone, so a
   * re-render with the same slot never clears a run in flight.
   *
   * `onCompleteRef` is cleared with the rest. It is a per-task closure belonging to whoever called
   * `setTask` on the *previous* slot, so leaving it set meant a task restored for the new slot
   * could finish and fire the old project's callback. Found by Bugbot on #1949. It lives below
   * this effect in source order, which is why this sits after its declaration rather than beside
   * the other re-seed state.
   */
  const seededSlotRef = useRef(slotId)
  useEffect(() => {
    if (seededSlotRef.current === slotId) return
    seededSlotRef.current = slotId
    const next = readStorage(slotId)
    setTaskId(next?.taskId ?? null)
    setQueuedTaskIds(next?.queuedTaskIds ?? [])
    setTaskMessage(next?.message ?? '')
    setTaskSuccessMessage(next?.successMessage ?? '')
    setTaskKind(next?.kind)
    handledRef.current = false
    pollCountRef.current = 0
    onCompleteRef.current = null
  }, [slotId])

  useEffect(() => {
    activeSlotsPolling.add(slotId)
    return () => {
      activeSlotsPolling.delete(slotId)
      window.dispatchEvent(new CustomEvent('slotTask:released', { detail: { slotId } }))
    }
  }, [slotId])

  // Desktop: pause when hidden; on focus reset backoff and clear if another tab already handled the task.
  useEffect(() => {
    if (IS_MOBILE) return
    const handleVisibilityChange = (): void => {
      const visible = document.visibilityState === 'visible'
      setIsVisible(visible)
      if (visible) {
        pollCountRef.current = 0
        if (taskIdRef.current !== null && readStorage(slotId) === null) {
          setTaskId(null)
          setTaskMessage('')
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => { document.removeEventListener('visibilitychange', handleVisibilityChange) }
  }, [slotId])

  // Desktop: storage event fires cross-tab immediately when another tab removes the key.
  //
  // A tab does NOT take on a run another tab starts: it would need its own poller to follow, and
  // two pollers on one task report it twice, demote whichever tab registered it, and leave the
  // key behind when they disagree about who cleans up. `setTask` reads the key instead, so a run
  // in flight is still never overwritten from here — the second tab just does not display it.
  // Found by review on #1939, after three defects in the version that did take it on.
  useEffect(() => {
    if (IS_MOBILE) return
    const handleStorage = (e: StorageEvent): void => {
      if (e.key === getStorageKey(slotId) && e.newValue === null) {
        setTaskId(null)
        setQueuedTaskIds([])
        setTaskMessage('')
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => { window.removeEventListener('storage', handleStorage) }
  }, [slotId])

  const setTask = useCallback((id: string | string[], message: string, successMessage?: string, onComplete?: () => void, kind?: SlotTaskKind): void => {
    const [first, ...queued] = Array.isArray(id) ? id : [id]
    // Callers pass a response field through; an empty array would otherwise register '' as a task
    // and leave the indicator stuck on a run that does not exist.
    if (!first) return
    // Nothing polls a task this user cannot read: both this hook's query and `GlobalSlotTaskPoller`
    // are gated on the global role, and `checkTaskNow` returns early without clearing. Writing the
    // key would hold `isTaskRunning` true for good and survive every reload — harmless while only
    // rare bulk actions wrote it, not once the ordinary section add does. Found by review on #1939.
    if (!isGlobalRole) return
    // One key per slot, so registering here replaces whatever is in it. Losing a bulk reopen or
    // go-live that way costs the admin its per-section outcomes — the only place refusals are
    // ever reported — so the run already in flight keeps the key and this one goes untracked
    // rather than the other way round. Callers that must not be dropped ask `checkIfBlocked()`
    // first, which both reports the incumbent and clears it if it has actually finished.
    //
    // The key is read here rather than trusted to local state: a tab that has not processed the
    // storage event yet — or any mobile tab, which never listens for one — holds no task id while
    // the key does, and a hook whose `slotId` argument has just changed (the add-to-project modal
    // switching projects) has not read the new key at all. Found by review on #1939.
    const incumbent = readStorage(slotId)
    if (incumbent && incumbent.taskId !== first) return
    writeStorage(slotId, { taskId: first, message, successMessage, queuedTaskIds: queued.length > 0 ? queued : undefined, kind })
    handledRef.current = false
    pollCountRef.current = 0
    onCompleteRef.current = onComplete ?? null
    setTaskId(first)
    setQueuedTaskIds(queued)
    setTaskMessage(message)
    setTaskSuccessMessage(successMessage ?? '')
    setTaskKind(kind)
  }, [slotId, isGlobalRole])

  /**
   * Moves to the next queued id without reporting anything: the queue is one background run to the
   * user, so `onTerminal` and the completion callback belong to its last task only.
   */
  const advanceTask = useCallback((): void => {
    const [next, ...rest] = queuedTaskIdsRef.current
    writeStorage(slotId, {
      taskId: next,
      message: taskMessageRef.current,
      successMessage: taskSuccessMessageRef.current || undefined,
      queuedTaskIds: rest.length > 0 ? rest : undefined,
      kind: taskKindRef.current
    })
    handledRef.current = false
    pollCountRef.current = 0
    setTaskId(next)
    setQueuedTaskIds(rest)
  }, [slotId])

  const clearTask = useCallback((): void => {
    localStorage.removeItem(getStorageKey(slotId))
    onCompleteRef.current = null
    setTaskId(null)
    setQueuedTaskIds([])
    setTaskMessage('')
    setTaskSuccessMessage('')
    setTaskKind(undefined)
  }, [slotId])

  const emitTerminal = useCallback((
    status: SlotTaskTerminalStatus,
    data?: TaskPollData,
    taskMetadata?: ITaskMetadata
  ): void => {
    const fallbackMessage = taskSuccessMessageRef.current || taskMessageRef.current
    onTerminalRef.current({ status, data, taskMetadata, fallbackMessage, kind: taskKindRef.current })
    if (status === 'SUCCESSFUL' || status === 'WARNING') {
      onCompleteRef.current?.()
    }
  }, [])

  // Instant check: fires callback + clears state on terminal status; uses handledRef to prevent double-fire with polling.
  const checkTaskNow = useCallback(async (): Promise<boolean> => {
    const currentTaskId = taskIdRef.current
    if (!currentTaskId) return false
    if (!isGlobalRole) return false
    try {
      const result = await getTaskById(currentTaskId)
      const status = result?.data?.status as string | undefined
      if (!status || !isTerminal(status)) return true
      // Polling can move the run on while this request is in flight — advancing the queue, or
      // reporting and clearing the last task. Either way this result is about a task that is no
      // longer the current one, and acting on it would report a run that is still going and wipe
      // the ids it had left. Found by review on #1939, where the queue-empty check alone let an
      // already-advanced queue fall through to the terminal path below.
      if (taskIdRef.current !== currentTaskId) return taskIdRef.current !== null
      if (status === 'SUCCESSFUL' && queuedTaskIdsRef.current.length > 0) {
        advanceTask()
        return true
      }
      // `finally`: the task is finished either way, and a throw in the toast or the completion
      // callback used to leave the localStorage key behind. `GlobalSlotTaskPoller` then re-polls
      // the same finished task on the next load and re-throws, so one bad payload turned into an
      // error page that survived every reload.
      try {
        if (!handledRef.current) {
          handledRef.current = true
          emitTerminal(
            status as SlotTaskTerminalStatus,
            result?.data?.data,
            result?.data?.taskMetadata
          )
        }
      } catch (error) {
        // Caught here rather than left to the outer `catch`, which belongs to `getTaskById` and
        // answers "still running?" with `true`. For a task this function has just cleared that
        // answer is wrong twice over: `checkIfBlocked` opens the blocked modal over a finished
        // run, and refuses the next one the admin starts. The task finished; only reporting it
        // failed, so the honest answer stays `false`.
        console.error('Slot task finished but its result could not be reported:', error)
      } finally {
        clearTask()
      }
      return false
    } catch {
      return true
    }
  }, [advanceTask, clearTask, emitTerminal, isGlobalRole])

  const { data: taskData } = useQuery({
    queryKey: ['slotBackgroundTask', slotId, taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('No taskId')
      return await getTaskById(taskId)
    },
    enabled: !!taskId && isVisible && isGlobalRole,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status as string | undefined
      if (status != null && isTerminal(status)) return false
      return Math.min(BASE_INTERVAL_MS * Math.pow(2, pollCountRef.current), MAX_INTERVAL_MS)
    }
  })

  useEffect(() => {
    if (!taskData) return
    const status = taskData?.data?.status as string | undefined
    if (!status) return
    if (!isTerminal(status)) {
      pollCountRef.current += 1
      return
    }
    if (handledRef.current) return
    handledRef.current = true
    // A failure ends the whole queue: the user has been told this run did not finish, and a
    // second outcome for the same action would only muddy that.
    if (status === 'SUCCESSFUL' && queuedTaskIdsRef.current.length > 0) {
      advanceTask()
      return
    }
    // See `checkTaskNow`: clearing in `finally` is what stops a throw here from stranding the
    // task in localStorage for the global poller to replay on every subsequent load.
    try {
      emitTerminal(
        status as SlotTaskTerminalStatus,
        taskData?.data?.data,
        taskData?.data?.taskMetadata
      )
    } finally {
      clearTask()
    }
  }, [taskData, advanceTask, clearTask, emitTerminal])

  return {
    isTaskRunning: !!taskId,
    taskMessage,
    setTask,
    clearTask,
    checkTaskNow
  }
}
